import React, { useCallback, useEffect, useRef, useState } from "react";
interface ClockProps {
    timeZone?: string;
}

const Clock: React.FC<ClockProps> = ({ timeZone }) => {
    const [hoursMinutes, setHoursMinutes] = useState("");
    const [seconds, setSeconds] = useState("");
    const [date, setDate] = useState("");

    // Keep the interval ref so we can clear it if needed
    const timerRef = useRef<NodeJS.Timer | null>(null);

    const getHoursMinutesString = useCallback(
        (date: Date) => {
            return date
                .toLocaleTimeString("en-US", {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: true,
                    timeZone: timeZone || undefined,
                })
                .replace("AM", "")
                .replace("PM", "")
                .replace(" ", "");
        },
        [timeZone]
    );

    const getSecondsString = useCallback(
        (date: Date) => {
            let seconds = date.toLocaleTimeString("en-US", {
                second: "2-digit",
                timeZone: timeZone || undefined,
            });

            if (seconds.length === 1) {
                seconds = "0" + seconds;
            }

            return seconds;
        },
        [timeZone]
    );

    const getDateString = useCallback(
        (date: Date) => {
            const weekDay = date
                .toLocaleDateString("en-US", {
                    weekday: "long",
                    timeZone: timeZone || undefined,
                })
                .toUpperCase();
            const month = date
                .toLocaleDateString("en-US", {
                    month: "short",
                    timeZone: timeZone || undefined,
                })
                .toUpperCase();
            const day = date.toLocaleDateString("en-US", {
                day: "numeric",
                timeZone: timeZone || undefined,
            });
            const year = date.getFullYear();

            return `${weekDay}, ${month} ${day}, ${year}`;
        },
        [timeZone]
    );

    const tickClock = useCallback(() => {
        const newDate = new Date();

        setHoursMinutes(getHoursMinutesString(newDate));
        setSeconds(getSecondsString(newDate));
        setDate(getDateString(newDate));
    }, [getHoursMinutesString, getSecondsString, getDateString]);

    useEffect(() => {
        // Update clock immediately on mount
        tickClock();

        timerRef.current = setInterval(tickClock, 1000);

        // Clean up interval on unmount
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
        // Only run once on mount
    }, [tickClock]);

    return (
        <div className="flex flex-col items-end">
            <div className="flex flex-row items-baseline justify-end">
                <div className="text-white text-8xl font-plot-open-sans font-bold">{hoursMinutes}</div>
                <div className="text-plot-orange text-5xl font-bold font-plot-open-sans">:</div>
                <div className="text-plot-orange text-5xl font-bold font-plot-roboto-mono">{seconds}</div>
            </div>
            <div className="text-plot-white text-xl font-plot-open-sans font-bold text-center">{date}</div>
        </div>
    );
};

export default Clock;
